import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { setAppTimezone, detectBrowserTimezone } from "../lib/utils";

const AuthCtx = createContext(null);

// Centralizes "user is now logged in" side-effects:
//   • Updates the shared timezone for all date formatters.
//   • On first login from this device, auto-detect the browser tz and
//     persist it through PATCH /users/me if it differs from the stored
//     preference. Defaults to America/New_York if neither is set.
async function _afterUserLoad(u) {
  if (!u) return;
  const stored = u.timezone || "America/New_York";
  setAppTimezone(stored);

  // One-time per device: if the browser tz differs from stored, suggest a sync.
  // Demo accounts don't have a real backend user — skip.
  if (u.auth_type === "demo") return;
  const flagKey = `ld_tz_synced_${u.id || u.email}`;
  if (localStorage.getItem(flagKey)) return;
  const detected = detectBrowserTimezone();
  if (detected && detected !== stored) {
    try {
      await api.patch("/users/me", { timezone: detected });
      setAppTimezone(detected);
      // Optimistic: mutate the cached user object so subsequent reads see it.
      u.timezone = detected;
    } catch (e) {
      // Best-effort timezone sync — fine to swallow. Worst case the user
      // can set it manually on the Profile page.
      console.debug("Auto-tz sync skipped:", e?.message || e);
    }
  }
  localStorage.setItem(flagKey, "1");
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("ld_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      await _afterUserLoad(data);
      setUser(data);
    } catch {
      localStorage.removeItem("ld_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // ---- LOGIN (multi-phase) -----------------------------------------------
  // Returns one of:
  //   { ok: true, user }                              (regular fully-authed)
  //   { ok: true, mfaSetupRequired: true, setupToken, email }
  //   { ok: true, mfaRequired: true, mfaToken, email }
  //   { ok: false, error }
  const login = async (email, password, turnstileToken) => {
    try {
      const { data } = await api.post("/auth/login", {
        email, password, turnstile_token: turnstileToken,
      });
      if (data.mfa_setup_required) {
        sessionStorage.setItem("ld_mfa_setup_token", data.mfa_setup_token);
        return { ok: true, mfaSetupRequired: true, setupToken: data.mfa_setup_token, email: data.email };
      }
      if (data.mfa_required) {
        sessionStorage.setItem("ld_mfa_token", data.mfa_token);
        return { ok: true, mfaRequired: true, mfaToken: data.mfa_token, email: data.email };
      }
      // Old single-shot path (shouldn't happen now, but defensive)
      if (data.token) {
        localStorage.setItem("ld_token", data.token);
        setUser(data.user);
        return { ok: true, user: data.user };
      }
      return { ok: false, error: "Unexpected login response" };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const demoLogin = async () => {
    try {
      const { data } = await api.post("/auth/demo");
      localStorage.setItem("ld_token", data.token);
      await _afterUserLoad(data.user);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  // ---- MFA setup flow ----------------------------------------------------
  const mfaSetupStart = async () => {
    const t = sessionStorage.getItem("ld_mfa_setup_token");
    if (!t) return { ok: false, error: "Setup window expired — please sign in again." };
    try {
      const { data } = await api.post("/auth/mfa/setup", {}, {
        headers: { Authorization: `Bearer ${t}` },
      });
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const mfaSetupVerify = async (code) => {
    const t = sessionStorage.getItem("ld_mfa_setup_token");
    if (!t) return { ok: false, error: "Setup window expired — please sign in again." };
    try {
      const { data } = await api.post("/auth/mfa/verify-setup", { code }, {
        headers: { Authorization: `Bearer ${t}` },
      });
      sessionStorage.removeItem("ld_mfa_setup_token");
      localStorage.setItem("ld_token", data.token);
      await _afterUserLoad(data.user);
      setUser(data.user);
      return { ok: true, user: data.user, recoveryCodes: data.recovery_codes };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  // ---- MFA verify flow ---------------------------------------------------
  const mfaVerify = async ({ code, recoveryCode }) => {
    const t = sessionStorage.getItem("ld_mfa_token");
    if (!t) return { ok: false, error: "Verification window expired — please sign in again." };
    try {
      const { data } = await api.post("/auth/mfa/verify",
        { code, recovery_code: recoveryCode },
        { headers: { Authorization: `Bearer ${t}` } },
      );
      sessionStorage.removeItem("ld_mfa_token");
      localStorage.setItem("ld_token", data.token);
      await _afterUserLoad(data.user);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  // ---- Invitation acceptance --------------------------------------------
  // Returns { ok, mfaSetupRequired, setupToken, email } or { ok:false, error }
  const acceptInvite = async (token, name, password, turnstileToken) => {
    try {
      const { data } = await api.post("/invitations/accept", {
        token, name, password, turnstile_token: turnstileToken,
      });
      if (data.mfa_setup_required) {
        sessionStorage.setItem("ld_mfa_setup_token", data.mfa_setup_token);
        return { ok: true, mfaSetupRequired: true, setupToken: data.mfa_setup_token, email: data.email };
      }
      if (data.token) {
        localStorage.setItem("ld_token", data.token);
        await _afterUserLoad(data.user);
        setUser(data.user);
        return { ok: true, user: data.user };
      }
      return { ok: false, error: "Unexpected response" };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  // ---- Force password change --------------------------------------------
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword, new_password: newPassword,
      });
      // Refresh user so must_change_password flips off in state
      const { data } = await api.get("/auth/me");
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = () => {
    localStorage.removeItem("ld_token");
    sessionStorage.removeItem("ld_mfa_token");
    sessionStorage.removeItem("ld_mfa_setup_token");
    setUser(null);
  };

  // Update name / timezone on the logged-in user. Returns the refreshed user.
  const updateProfile = async (patch) => {
    try {
      const { data } = await api.patch("/users/me", patch);
      setAppTimezone(data.timezone || "America/New_York");
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const refresh = fetchMe;

  // Memoize the context value so consumers don't re-render on every
  // AuthProvider render. The methods themselves are stable identity-wise
  // because they only read state via setters/refs from closures over
  // each render, but the object that wraps them was previously rebuilt
  // on every render — that's the perf issue the code review flagged.
  const value = useMemo(() => ({
    user, loading,
    login, demoLogin, acceptInvite,
    mfaSetupStart, mfaSetupVerify, mfaVerify,
    changePassword, updateProfile,
    logout, refresh,
  // The method identities change every render, but consumers almost always
  // care about `user` and `loading` — that's what controls re-renders here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, loading]);

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
